import type { FaceTecCustomization } from 'facetec-sdk/FaceTecSDK.js/FaceTecCustomization';

const getFacetecCustomization = async (darkMode: boolean): Promise<FaceTecCustomization> => {
  const sdkImageDirectory = `${process.env.PUBLIC_URL}/facetec-sdk/FaceTec_images`;
  const { FaceTecSDK } = await import('facetec-sdk');

  // For Color Customization
  interface FacetecTheme {
    outerBackgroundColor: string;
    frameColor: string;
    borderColor: string;
    ovalColor: string;
    dualSpinnerColor: string;
    textColor: string;
    subtextColor: string;
    buttonAndFeedbackBarColor: string;
    buttonAndFeedbackBarTextColor: string;
    buttonColorPressed: string;
  }

  const darkTheme: FacetecTheme = {
    outerBackgroundColor: '#1E293B',
    frameColor: '#1E293B',
    borderColor: '#D4D4D4',
    ovalColor: '#7C75FF',
    dualSpinnerColor: '#7C75FF',
    textColor: '#FFF1F2',
    subtextColor: '#D4D4D4',
    buttonAndFeedbackBarColor: '#7C75FF',
    buttonAndFeedbackBarTextColor: '#FFFFFF',
    buttonColorPressed: '#5B53FF',
  };

  const lightTheme: FacetecTheme = {
    outerBackgroundColor: '#FFFFFF',
    frameColor: '#FFFFFF',
    borderColor: '#D4D4D4',
    ovalColor: '#7C75FF',
    dualSpinnerColor: '#7C75FF',
    textColor: '#0A0919',
    subtextColor: '#737373',
    buttonAndFeedbackBarColor: '#7C75FF',
    buttonAndFeedbackBarTextColor: '#FFFFFF',
    buttonColorPressed: '#5B53FF',
  };

  const customizationTheme: FacetecTheme = darkMode ? darkTheme : lightTheme;

  // For Frame Corner Radius Customization
  const frameCornerRadius = '15px';

  // For Button Corner Radius Customization
  const buttonCornerRadius = '20px';

  // For Cancel Button Customization
  // Re-use the transparent 1x1 logo image to make the cancel button disappear
  const cancelButtonImage = `${sdkImageDirectory}/app_logo.png`;
  const cancelButtonLocation = FaceTecSDK.FaceTecCancelButtonLocation.TopLeft;

  // Re-use the transparent 1x1 logo image to make the cancel button disappear
  const enterFullScreenImage = `${sdkImageDirectory}/app_logo.png`;

  // For image Customization
  const yourAppLogoImage = `${sdkImageDirectory}/app_logo.png`;
  const securityWatermarkImage = FaceTecSDK.FaceTecSecurityWatermarkImage.FaceTec_ZoOm;

  // Set a customization that enables use in iframes
  const SDKInIFrameFeatureFlag = { ac_ziif: '991d6c1c-5e5c-4e32-89c7-1ddfac46572e' };
  const customization = new FaceTecSDK.FaceTecCustomization([SDKInIFrameFeatureFlag]);

  // Set Fullscreen Prompt Customization
  customization.enterFullScreenCustomization.buttonCornerRadius = buttonCornerRadius;
  customization.enterFullScreenCustomization.borderCornerRadius = frameCornerRadius;
  customization.enterFullScreenCustomization.foregroundColor = customizationTheme.textColor;
  customization.enterFullScreenCustomization.backgroundColors = customizationTheme.outerBackgroundColor;
  customization.enterFullScreenCustomization.buttonTextNormalColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.enterFullScreenCustomization.buttonTextDisabledColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.enterFullScreenCustomization.buttonTextHighlightColor =
    customizationTheme.buttonAndFeedbackBarTextColor;
  customization.enterFullScreenCustomization.buttonBackgroundNormalColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.enterFullScreenCustomization.buttonBackgroundHighlightColor = customizationTheme.buttonColorPressed;

  customization.enterFullScreenCustomization.borderColor = customizationTheme.borderColor;
  customization.enterFullScreenCustomization.enterFullScreenImage = enterFullScreenImage;

  // Set Frame Customization
  customization.frameCustomization.borderCornerRadius = frameCornerRadius;
  customization.frameCustomization.backgroundColor = customizationTheme.frameColor;
  customization.frameCustomization.borderColor = customizationTheme.borderColor;

  // Set Overlay Customization
  customization.overlayCustomization.brandingImage = yourAppLogoImage;
  customization.overlayCustomization.backgroundColor = customizationTheme.outerBackgroundColor;

  // Set Guidance Customization
  customization.guidanceCustomization.backgroundColors = customizationTheme.frameColor;
  customization.guidanceCustomization.foregroundColor = customizationTheme.textColor;
  customization.guidanceCustomization.buttonBackgroundNormalColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.guidanceCustomization.buttonBackgroundDisabledColor = customizationTheme.buttonColorPressed;
  customization.guidanceCustomization.buttonBackgroundHighlightColor = customizationTheme.buttonColorPressed;
  customization.guidanceCustomization.buttonCornerRadius = buttonCornerRadius;
  customization.guidanceCustomization.buttonTextNormalColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.guidanceCustomization.buttonTextDisabledColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.guidanceCustomization.buttonTextHighlightColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.guidanceCustomization.retryScreenImageBorderColor = customizationTheme.borderColor;
  customization.guidanceCustomization.retryScreenOvalStrokeColor = customizationTheme.borderColor;
  customization.guidanceCustomization.readyScreenSubtextTextColor = customizationTheme.subtextColor;
  customization.guidanceCustomization.retryScreenSubtextTextColor = customizationTheme.subtextColor;

  // Set Oval Customization
  customization.ovalCustomization.strokeColor = customizationTheme.ovalColor;
  customization.ovalCustomization.progressColor1 = customizationTheme.dualSpinnerColor;
  customization.ovalCustomization.progressColor2 = customizationTheme.dualSpinnerColor;

  // Set Feedback Customization
  customization.feedbackCustomization.backgroundColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.feedbackCustomization.textColor = customizationTheme.buttonAndFeedbackBarTextColor;

  // Set Cancel Customization
  customization.cancelButtonCustomization.customImage = cancelButtonImage;
  customization.cancelButtonCustomization.location = cancelButtonLocation;

  // Set Security Watermark Customization
  customization.securityWatermarkCustomization.setSecurityWatermarkImage(securityWatermarkImage);

  // Set Result Screen Customization
  customization.resultScreenCustomization.backgroundColors = customizationTheme.frameColor;
  customization.resultScreenCustomization.foregroundColor = customizationTheme.textColor;
  customization.resultScreenCustomization.activityIndicatorColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.resultScreenCustomization.resultAnimationBackgroundColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.resultScreenCustomization.resultAnimationForegroundColor =
    customizationTheme.buttonAndFeedbackBarTextColor;
  customization.resultScreenCustomization.uploadProgressFillColor = customizationTheme.buttonAndFeedbackBarColor;

  // Set ID Scan Customization
  customization.idScanCustomization.selectionScreenBackgroundColors = customizationTheme.frameColor;
  customization.idScanCustomization.selectionScreenForegroundColor = customizationTheme.textColor;
  customization.idScanCustomization.reviewScreenBackgroundColors = customizationTheme.frameColor;
  customization.idScanCustomization.reviewScreenForegroundColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.idScanCustomization.reviewScreenTextBackgroundColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.idScanCustomization.captureScreenForegroundColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.idScanCustomization.captureScreenTextBackgroundColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.idScanCustomization.buttonBackgroundNormalColor = customizationTheme.buttonAndFeedbackBarColor;
  customization.idScanCustomization.buttonBackgroundDisabledColor = customizationTheme.buttonColorPressed;
  customization.idScanCustomization.buttonBackgroundHighlightColor = customizationTheme.buttonColorPressed;
  customization.idScanCustomization.buttonTextNormalColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.idScanCustomization.buttonTextDisabledColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.idScanCustomization.buttonTextHighlightColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.idScanCustomization.buttonCornerRadius = buttonCornerRadius;
  customization.idScanCustomization.captureScreenBackgroundColor = customizationTheme.frameColor;
  customization.idScanCustomization.captureFrameStrokeColor = customizationTheme.borderColor;

  // Set Initial Loading Customization
  customization.initialLoadingAnimationCustomization.backgroundColor = customizationTheme.buttonAndFeedbackBarTextColor;
  customization.initialLoadingAnimationCustomization.foregroundColor = customizationTheme.buttonAndFeedbackBarColor;

  return customization;
};

export default getFacetecCustomization;
